import {
  SiApple,
  SiAtlassian,
  SiDiscord,
  SiDropbox,
  SiFacebook,
  SiFigma,
  SiGithub,
  SiGitlab,
  SiGoogle,
  SiHuggingface,
  SiKakao,
  SiKick,
  SiLine,
  SiLinear,
  SiNaver,
  SiNotion,
  SiPaypal,
  SiReddit,
  SiRoblox,
  SiSalesforce,
  SiSlack,
  SiSpotify,
  SiTiktok,
  SiTwitch,
  SiVk,
  SiX,
  SiZoom,
} from '@icons-pack/react-simple-icons'
import { Button } from '@repo/ui/components/ui/button'
import { Input } from '@repo/ui/components/ui/input'
import { Label } from '@repo/ui/components/ui/label'
import { cn } from '@repo/ui/lib/utils'
import { Mail } from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'
import { FaAws, FaLinkedin, FaMicrosoft } from 'react-icons/fa6'

const authCardShell =
  'app-premium-panel relative overflow-hidden rounded-sm border-border/75 p-7 md:p-8'

export function AuthCard({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: ReactNode
}) {
  return (
    <div className={authCardShell}>
      <div className="relative space-y-6">
        <div className="space-y-1.5">
          <h1 className="app-display-title text-foreground">{title}</h1>
          <p className="text-base text-muted-foreground">{description}</p>
        </div>
        {children}
      </div>
    </div>
  )
}

export function AuthField({
  id,
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  autoComplete,
  required = true,
}: {
  id: string
  label: string
  type?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  autoComplete?: string
  required?: boolean
}) {
  return (
    <div className="space-y-2.5">
      <Label htmlFor={id} className="app-kicker text-muted-foreground">
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        className="h-12 rounded-[4px] border-border/70 bg-background/92 px-4 text-[0.98rem] text-foreground shadow-xs focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/20"
      />
    </div>
  )
}

export function AuthSubmit({
  loading,
  disabled,
  children,
}: {
  loading: boolean
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <Button
      type="submit"
      className={cn(
        'mt-2 h-12 w-full rounded-[4px] border border-primary/20 bg-primary text-base font-medium text-primary-foreground shadow-sm transition-all duration-300 hover:bg-primary/90 active:translate-y-px'
      )}
      disabled={loading || disabled}
    >
      {loading ? 'Opening workspace...' : children}
    </Button>
  )
}

export function AuthSecondaryAction({
  loading,
  disabled,
  children,
  onClick,
}: {
  loading?: boolean
  disabled?: boolean
  children: ReactNode
  onClick: () => void | Promise<void>
}) {
  return (
    <Button
      type="button"
      variant="outline"
      className="h-12 w-full rounded-[4px] border-border/70 bg-background/92 text-foreground shadow-xs transition-[background-color,border-color,transform] hover:bg-background hover:text-foreground active:translate-y-[1px]"
      disabled={loading || disabled}
      onClick={() => void onClick()}
    >
      {loading ? 'Sending link...' : children}
    </Button>
  )
}

export function AuthDivider({ label = 'or continue with' }: { label?: string }) {
  return (
    <div className="relative my-5">
      <div className="absolute inset-0 flex items-center">
        <span className="app-soft-divider" />
      </div>
      <div className="relative flex justify-center text-muted-foreground">
        <span className="app-kicker rounded-[3px] border border-border/60 bg-background/90 px-3 py-1 text-muted-foreground">
          {label}
        </span>
      </div>
    </div>
  )
}

export function AuthSocialButtons({
  providers,
  disabled,
  loadingProvider,
  onProvider,
}: {
  providers: string[]
  disabled?: boolean
  loadingProvider?: string | null
  onProvider: (provider: string) => void
}) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {providers.map((provider) => {
        const label = formatProviderName(provider)
        return (
          <Button
            key={provider}
            type="button"
            variant="outline"
            onClick={() => onProvider(provider)}
            disabled={disabled}
            className="h-12 rounded-[4px] border-border/70 bg-background/92 text-foreground shadow-xs transition-[background-color,border-color,transform] hover:bg-background hover:text-foreground active:translate-y-[1px]"
          >
            {getProviderIcon(provider)}
            <span>{loadingProvider === provider ? `${label}...` : label}</span>
          </Button>
        )
      })}
    </div>
  )
}

export function EmailSentState({ email, onResend }: { email: string; onResend: () => void }) {
  return (
    <div className={`${authCardShell} space-y-5 text-center`}>
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-[4px] border border-border/60 bg-primary/10">
        <Mail className="h-5 w-5 text-primary" />
      </div>
      <div className="space-y-2">
        <h1 className="app-display-title text-foreground">Check your inbox</h1>
        <p className="text-base text-muted-foreground">
          We sent an access link to <span className="font-medium text-foreground">{email}</span>.
        </p>
      </div>
      <Button
        type="button"
        variant="outline"
        className="h-12 w-full rounded-[4px] border-border/70 bg-background/92 text-foreground shadow-xs hover:bg-background hover:text-foreground"
        onClick={onResend}
      >
        Send another link
      </Button>
      <p className="text-sm text-muted-foreground">
        If it doesn&apos;t arrive, check spam or promotions and retry.
      </p>
    </div>
  )
}

function formatProviderName(provider: string): string {
  return provider
    .split(/[-_]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function getProviderIcon(provider: string) {
  const Icon = PROVIDER_ICONS[provider]
  if (Icon) return <Icon className="h-4 w-4" />
  return <span className="text-xs font-semibold">{formatProviderName(provider).charAt(0)}</span>
}

export function formatAuthProviderName(provider: string): string {
  return formatProviderName(provider)
}

export function AuthProviderIcon({
  provider,
  className = 'h-4 w-4',
}: {
  provider: string
  className?: string
}) {
  const Icon = PROVIDER_ICONS[provider]
  if (Icon) return <Icon className={className} />
  return <span className="text-xs font-semibold">{formatProviderName(provider).charAt(0)}</span>
}

const PROVIDER_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  github: SiGithub,
  apple: SiApple,
  atlassian: SiAtlassian,
  cognito: FaAws,
  discord: SiDiscord,
  facebook: SiFacebook,
  figma: SiFigma,
  microsoft: FaMicrosoft,
  google: SiGoogle,
  huggingface: SiHuggingface,
  slack: SiSlack,
  spotify: SiSpotify,
  twitch: SiTwitch,
  twitter: SiX,
  dropbox: SiDropbox,
  kick: SiKick,
  linear: SiLinear,
  linkedin: FaLinkedin,
  gitlab: SiGitlab,
  tiktok: SiTiktok,
  reddit: SiReddit,
  roblox: SiRoblox,
  salesforce: SiSalesforce,
  vk: SiVk,
  zoom: SiZoom,
  notion: SiNotion,
  kakao: SiKakao,
  naver: SiNaver,
  line: SiLine,
  paypal: SiPaypal,
}
